import { createFeatureSelector, createReducer, createSelector, on } from '@ngrx/store';
import { DateTime } from 'luxon';
import type { ExportRequest } from './export-downloads.actions';
import * as ExportDownloadsActions from './export-downloads.actions';

const EXPORT_TTL_DAYS = 1; // 24 hours

export const exportDownloadsFeatureKey = 'export-downloads';

export interface State {
  pending: ExportRequest[];
  ready: ExportRequest[];
  showDownloads: boolean;
  error: unknown;
}

export const initialState: State = {
  pending: [],
  ready: [],
  showDownloads: false,
  error: null,
};

export const exportDownloadsReducer = createReducer(
  initialState,
  on(ExportDownloadsActions.requestExportSuccess, (state, { exportId, jobId, jobTitle }) => ({
    ...state,
    pending: [
      ...state.pending,
      { exportId, jobId, jobTitle },
    ],
    showDownloads: true,
    error: null,
  })),
  on(ExportDownloadsActions.requestExportFailure, (state, { error }) => ({
    ...state,
    error,
  })),
  on(ExportDownloadsActions.exportReady, (state, { exportId, jobId, filename, downloadUrl, creationTime }) => {
    const pending = state.pending.find((p) => p.exportId === exportId);
    if (!pending) {
      return state;
    }
    const jobTitle = pending?.jobTitle ?? '';
    const readyItem: ExportRequest = {
      exportId,
      jobId,
      jobTitle,
      filename,
      downloadUrl,
      creationTime,
    };
    return {
      ...state,
      pending: state.pending.filter((p) => p.exportId !== exportId),
      ready: [...state.ready.filter((r) => r.exportId !== exportId), readyItem],
      error: null,
    };
  }),
  on(ExportDownloadsActions.exportFailed, (state, { exportId }) => {
    if (!state.pending.some((p) => p.exportId === exportId)) {
      return state;
    }
    return {
      ...state,
      pending: state.pending.filter((p) => p.exportId !== exportId),
    };
  }),
  on(ExportDownloadsActions.removeExport, (state, { exportId }) => {
    const newPending = state.pending.filter((p) => p.exportId !== exportId);
    const newReady = state.ready.filter((r) => r.exportId !== exportId);
    const count = newPending.length + newReady.length;
    return {
      ...state,
      pending: newPending,
      ready: newReady,
      showDownloads: count > 0 ? state.showDownloads : false,
    };
  }),
  on(ExportDownloadsActions.openDownloadsDropdown, (state) => ({
    ...state,
    showDownloads: true,
  })),
  on(ExportDownloadsActions.closeDownloadsDropdown, (state) => ({
    ...state,
    showDownloads: false,
  })),
  on(ExportDownloadsActions.updateExportCreationTime, (state, { exportId, creationTime }) => ({
    ...state,
    ready: state.ready.map((r) =>
      r.exportId === exportId ? { ...r, creationTime } : r
    ),
  })),
  on(ExportDownloadsActions.pruneExpiredExports, (state, { ttlDays = EXPORT_TTL_DAYS }) => {
    const cutoff = DateTime.now().minus({ days: ttlDays });
    const stillValid = (item: ExportRequest) => {
      if (!item.creationTime) return true;
      const created = DateTime.fromISO(item.creationTime);
      return created > cutoff;
    };
    const newReady = state.ready.filter(stillValid);
    const count = state.pending.length + newReady.length;
    return {
      ...state,
      ready: newReady,
      showDownloads: count > 0 ? state.showDownloads : false,
    };
  })
);

export const selectExportDownloadsState = createFeatureSelector<State>(exportDownloadsFeatureKey);

const selectReadyExportsRaw = createSelector(
  selectExportDownloadsState,
  (state) => state.ready
);

export const selectReadyExports = createSelector(
  selectReadyExportsRaw,
  (ready) => {
    const cutoff = DateTime.now().minus({ days: EXPORT_TTL_DAYS });
    return ready.filter((item) => {
      if (!item.creationTime) return true;
      const created = DateTime.fromISO(item.creationTime);
      return created > cutoff;
    });
  }
);

export const selectShowDownloads = createSelector(
  selectExportDownloadsState,
  (state) => state.showDownloads ?? false
);

export const selectPendingExports = createSelector(
  selectExportDownloadsState,
  (state) => state.pending
);

export const selectAllExports = createSelector(
  selectExportDownloadsState,
  selectReadyExports,
  (state, readyFiltered) => {
    const pending = state.pending.map((p) => ({ ...p, isPending: true }));
    const ready = readyFiltered.map((r) => ({ ...r, isPending: false }));
    return [...pending, ...ready];
  }
);

export const selectExportsCount = createSelector(
  selectExportDownloadsState,
  selectReadyExports,
  (state, readyFiltered) => state.pending.length + readyFiltered.length
);
